import { NgOptimizedImage } from '@angular/common'
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router, RouterLink } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { EMPTY } from 'rxjs'
import { catchError, finalize } from 'rxjs/operators'

import { AuthService } from '../../services/auth/auth.service'
import { SubscriptionService } from '../../services/subscription/subscription.service'
import { ContentAccessEnum, UserRole } from '../../types/types'

type SubscribeButtonAction =
  | 'subscribe'
  | 'manageSubscriptions'
  | 'manageSeats'
  | 'requestAccess'
  | 'unpaidInvoice'
  | 'subscriptionNotStarted'
  | 'subscribed'

type SubscribeButtonRole = 'billingAdmin' | 'accountAdmin' | 'user'

@Component({
  selector: 'dhp-subscribe-button',
  templateUrl: './subscribe-button.component.html',
  imports: [TranslatePipe, RouterLink, NgOptimizedImage],
})
export class SubscribeButtonComponent {
  // Dependencies
  private readonly destroyRef = inject(DestroyRef)
  private readonly authService = inject(AuthService)
  private readonly router = inject(Router)
  private readonly subscriptionService = inject(SubscriptionService)
  private readonly translateService = inject(TranslateService)

  // Input
  readonly magazineId = input.required<number>()
  readonly userAccessLevel = input.required<ContentAccessEnum | null>()
  readonly canPurchaseArticle = input<boolean>(false)

  readonly getDetailedInfo = output<string | null>()
  readonly action = computed<SubscribeButtonAction | null>(() =>
    this.getAction(this.userAccessLevel())
  )
  readonly resolvedLoggedOutActionLabelKey = computed(
    () => 'components.subscribeButton.actions.subscribe'
  )

  // State variables
  isRequestSeatError = signal<boolean>(false)
  isRequestSeatPending = signal<boolean>(false)
  isRequestSeatSuccess = signal<boolean>(false)

  constructor() {
    effect(() => {
      this.userAccessLevel()
      this.canPurchaseArticle()
      this.authService.authCheckCompleted()
      this.authService.currentUser()

      this.getDetailedInfo.emit(
        this.getDetailedInfoText(this.userAccessLevel())
      )
    })

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getDetailedInfo.emit(
          this.getDetailedInfoText(this.userAccessLevel())
        )
      })
  }

  requestSeat(): void {
    // Prevent the frontend from sending multiple requests when the user clicks the button multiple times
    if (this.isRequestSeatPending()) return
    // Set the state to true, so the logic cannot be retriggered until ready again
    this.isRequestSeatPending.set(true)

    // Reset other states
    this.isRequestSeatSuccess.set(false)
    this.isRequestSeatError.set(false)

    // Try to request seat request
    this.subscriptionService
      .requestSeat(this.magazineId())
      .pipe(
        catchError((error: unknown) => {
          // Log the error and set the request seat error state to true, so the UI can display an error message
          console.error(error)
          this.isRequestSeatError.set(true)

          return EMPTY
        }),
        finalize(() => {
          // Set pending state to false after each request
          this.isRequestSeatPending.set(false)
        })
      )
      .subscribe(() => {
        // Set success state to true after a successful request
        this.isRequestSeatSuccess.set(true)
      })
  }

  private getDetailedInfoText(
    userAccessLevel: ContentAccessEnum | null
  ): string | null {
    const detailInfoKey = this.getDetailedInfoKey(userAccessLevel)
    if (!detailInfoKey) {
      return null
    }

    return String(this.translateService.instant(detailInfoKey))
  }

  private getDetailedInfoKey(
    userAccessLevel: ContentAccessEnum | null
  ): string | null {
    if (!this.authService.authCheckCompleted()) {
      return null
    }

    if (!this.authService.isAuthenticated()) {
      return 'components.subscribeButton.details.notLoggedIn'
    }

    const accessRole = this.getAccessRole()
    switch (userAccessLevel) {
      case null:
        return `components.subscribeButton.details.${accessRole}.noSubscription`
      case ContentAccessEnum.INVOICE_NOT_PAID:
        return 'components.subscribeButton.details.unpaidInvoice'
      case ContentAccessEnum.SUBSCRIPTION_NOT_STARTED:
        return 'components.subscribeButton.details.subscriptionNotStarted'
      case ContentAccessEnum.SEAT_AVAILABLE:
      case ContentAccessEnum.SEAT_UNAVAILABLE:
        return `components.subscribeButton.details.${accessRole}.noSeat`
      case ContentAccessEnum.SEAT_ASSIGNED:
        return null
      default:
        return null
    }
  }

  private getAction(
    userAccessLevel: ContentAccessEnum | null
  ): SubscribeButtonAction | null {
    if (!this.authService.authCheckCompleted()) {
      return null
    }

    if (!this.authService.isAuthenticated()) {
      return 'subscribe'
    }

    const accessRole = this.getAccessRole()
    switch (userAccessLevel) {
      case ContentAccessEnum.SEAT_UNAVAILABLE:
        if (accessRole === 'billingAdmin') {
          return 'manageSubscriptions'
        }

        if (accessRole === 'accountAdmin') {
          return 'manageSeats'
        }

        return 'requestAccess'
      case ContentAccessEnum.INVOICE_NOT_PAID:
        return 'unpaidInvoice'
      case ContentAccessEnum.SUBSCRIPTION_NOT_STARTED:
        return 'subscriptionNotStarted'
      case ContentAccessEnum.SEAT_ASSIGNED:
      case ContentAccessEnum.SEAT_AVAILABLE:
        return 'subscribed'
      case null: {
        const user = this.authService.currentUser()
        if (user?.organization) {
          return accessRole === 'billingAdmin' ? 'subscribe' : null
        }
        return 'subscribe'
      }
      default:
        return null
    }
  }

  private getAccessRole(): SubscribeButtonRole {
    const roles = this.authService.currentUser()?.roles ?? []
    if (roles.includes(UserRole.BillingAdmin)) {
      return 'billingAdmin'
    }
    if (roles.includes(UserRole.AccountAdmin)) {
      return 'accountAdmin'
    }
    return 'user'
  }
}
